import { findIndex, getStringWidth } from "@dhx/ts-common/core";
import { awaitRedraw, el } from "@dhx/ts-common/dom";
import { IEventSystem } from "@dhx/ts-common/events";
import { Grid, GridEvents } from "@dhx/ts-grid";
import { DataPage } from "@xbs/muon";
import { getFormattedValue } from "./helpers/format";
import {
	getCellIds,
	getCellIndex,
	getCellInfo,
	getCellNameById,
	getCellNameByIndex,
	getCellsArray,
	getRangeArray,
	getRangeIndexes,
	isRangeId,
	isCellFixed,
	isCellSpaned,
} from "./helpers/main";
import { getLastCopyingCell } from "./helpers/selection";
import { Spreadsheet } from "./Spreadsheet";
import {
	Actions,
	IBufferManager,
	ICol,
	IRow,
	ISelection,
	ISpan,
	SpreadsheetEvents,
	IRangeIndex,
} from "./types";

export interface ISelectedCell {
	row: string;
	col: string;
	cell: string;
	edit?: boolean;
}

export class Selection implements ISelection {
	private _spreadsheet: Spreadsheet;
	private _grid: Grid;
	private _bufferManager: IBufferManager;
	private _events: IEventSystem<SpreadsheetEvents>;
	private _mousePressed: boolean;
	private _resizedColumns: any;
	private _resizedRows: any;
	private _pressedArea: "cell" | "header" | "index" | "fillHandle";
	private _cellsToCopy: string;
	private _activePage: DataPage;
	private _searchCell: string;
	private _searchedCells: string[];
	private _filteredCells: string;
	private _prevHeaderCss: string[];

	constructor(spreadsheet: Spreadsheet, grid: Grid, bufferManager: IBufferManager, activePage: DataPage) {
		this._spreadsheet = spreadsheet;
		this._grid = grid;
		this._bufferManager = bufferManager;
		this._events = spreadsheet.events;
		this._activePage = activePage;
		this._setHandlers();
	}

	setSelectedCell(cell: string) {
		if (!cell) {
			return;
		}
		if (!this._events.fire(SpreadsheetEvents.beforeSelectionSet, [cell])) {
			return;
		}

		this._removeHeadersCss();

		const { merged } = this._activePage.getMeta() as { merged: ISpan[] };
		const cells = cell.split(",");
		const selected = cells.map(c => {
			let newCell = c;
			if (!merged?.length || (this._pressedArea && this._pressedArea !== "cell")) return newCell;
			if (isRangeId(c)) {
				const cells = getRangeArray(c, this._grid);
				const set: Set<ISpan> = new Set();
				cells.forEach(rangeCell => {
					const span = isCellSpaned(rangeCell, merged, this._grid);
					if (span) {
						set.add(span);
					}
				});
				if (set.size) {
					const firstCellIds = getCellIds(cells[0], this._grid);
					const lastCellIds = getCellIds(cells[cells.length - 1], this._grid);
					let { row: firstRow, col: firstCol } = firstCellIds;
					let { row: lastRow, col: lastCol } = lastCellIds;
					Array.from(set).forEach(({ from, to }) => {
						if (from.row < firstRow) {
							firstRow = from.row;
						}
						if (from.column < firstCol) {
							firstCol = from.column;
						}
						if (to.row > lastRow) {
							lastRow = to.row;
						}
						if (to.column > lastCol) {
							lastCol = to.column;
						}
					});
					const firstCell = getCellNameByIndex(firstRow, firstCol + 1, this._grid);
					const lastCell = getCellNameByIndex(lastRow, lastCol + 1, this._grid);
					newCell = `${firstCell}:${lastCell}`;
				}
			} else {
				const span = isCellSpaned(c, merged, this._grid);
				if (span) {
					newCell = getCellNameByIndex(span.from.row, span.from.column + 1, this._grid);
				}
			}
			return newCell;
		});

		this._activePage.setMeta({ selectedCells: selected });

		let nextFocused = cells[cells.length - 1];
		if (isRangeId(nextFocused)) {
			nextFocused = nextFocused.split(":")[0];
		}
		this.setFocusedCell(nextFocused);

		this._setHeadersCss();

		if (!this._mousePressed) {
			this._events.fire(SpreadsheetEvents.afterSelectionSet, [this.getSelectedCell()]);
		} else {
			this._grid.paint();
		}
	}

	getSelectedCell(): string {
		const selected = this._activePage.getMeta().selectedCells;
		if (selected && selected.length) {
			return selected.join(",");
		}
	}

	getFocusedCell(): string {
		const focusedCell = this._activePage.getMeta().focusedCell;
		if (focusedCell && focusedCell.cell) {
			return focusedCell.cell;
		}
	}

	setFocusedCell(cell: string) {
		if (!cell) return;
		cell = cell.toUpperCase();

		if (!this._events.fire(SpreadsheetEvents.beforeFocusSet, [cell])) {
			return;
		}
		const { row, col } = getCellIds(cell, this._grid);
		this._activePage.setMeta({
			focusedCell: {
				row,
				col,
				cell,
				edit: false,
			},
		});

		this._events.fire(SpreadsheetEvents.afterFocusSet, [cell]);
	}

	removeSelectedCell(cell?: string) {
		const selected = this.getSelectedCell();
		if (selected) {
			if (!cell) {
				this._activePage.setMeta({ selectedCells: null });
			} else {
				const selectedCells = selected.split(",");
				const splitedTarget = cell.split(",");
				const cellsToRemove = [];
				splitedTarget.forEach(c => {
					if (isRangeId(c)) {
						const cells = getCellsArray(c, this._grid);
						cells.forEach(i => {
							cellsToRemove.push(i);
						});
						return;
					}
					cellsToRemove.push(c);
				});
				const next = [];
				selectedCells.forEach(c => {
					if (isRangeId(c)) {
						const cells = getCellsArray(c, this._grid);
						cellsToRemove.forEach(i => {
							const index = cells.indexOf(i);
							if (index > -1) {
								cells[index] = null;
							}
						});
						let firstCell = null;
						cells.forEach((i, index) => {
							if (!firstCell) {
								firstCell = i;
								return;
							}
							if (!i) {
								next.push(`${firstCell}:${cells[index - 1]}`);
								firstCell = null;
							}
							if (i && index === cells.length - 1) {
								next.push(`${firstCell}:${cells[index]}`);
							}
						});
						return;
					}
					if (!cellsToRemove.includes(c)) {
						next.push(c);
					}
				});
				if (next.length) {
					this.setSelectedCell(next.join(","));
					return;
				} else {
					this._activePage.setMeta({ selectedCells: null });
				}
			}
			this._grid.paint();
		}
	}

	private _isInRange(cell: string, range: string): boolean {
		if (!range || !cell || !range.includes(":")) {
			return false;
		}
		const { row, col } = getCellIndex(cell, this._grid);
		const { start, end } = getRangeIndexes(range, this._grid);

		const between = (x, min, max) => {
			return (x - min) * (x - max) <= 0;
		};

		if (start && end) {
			return between(row, start.row, end.row) && between(col, start.col, end.col);
		}

		return false;
	}

	_setSearchCell(index: number) {
		if (!this._searchedCells[index]) return;
		this._searchCell = this._searchedCells[index];
		const { row, col } = getCellIndex(this._searchCell, this._grid, true);
		this._grid.scrollTo(row + 1, col);
	}

	_setSearchedArray(cells: string[]) {
		this._searchedCells = cells;
	}

	_removeSearchCell() {
		this._searchCell = null;
		this._searchedCells = [];
		this._grid.paint();
	}

	private _removeHeadersCss() {
		const selected = this.getSelectedCell();
		if (selected) {
			this._prevHeaderCss.forEach(cellName => {
				const index = getCellIndex(cellName, this._grid);

				const row = this._grid.data.getId(index.row);

				if (row !== undefined) {
					this._grid.removeRowCss(row, "dhx_selected_row");
				}

				const col = this._grid.config.columns[index.col];
				if (col !== undefined) {
					col.header[0].css = col.header[0].css.replace(" dhx_selected_header", "");
				}
			});
			const focusedCell = this._activePage.getMeta().focusedCell;
			if (this._grid.data.exists(focusedCell.row)) {
				this._grid.removeRowCss(focusedCell.row, "dhx_selected_row");
			}
		}
		if (this._filteredCells) {
			this._spreadsheet.eachCell(cellName => {
				const index = getCellIndex(cellName, this._grid);

				const row = this._grid.data.getId(index.row);

				if (row !== undefined) {
					this._grid.removeRowCss(row, "dhx_filtered_row");
				}

				const col = this._grid.config.columns[index.col];
				if (col !== undefined) {
					col.header[0].css = col.header[0].css.replace(" dhx_filtered_header", "");
				}
			}, this._filteredCells);
			this._filteredCells = "";
		}
	}

	private _setHeadersCss() {
		const selected = this.getSelectedCell();
		const { merged, filter } = this._activePage.getMeta();
		this._prevHeaderCss = [];
		if (selected) {
			const spans: ISpan[] = [];
			this._spreadsheet.eachCell(cellName => {
				const index = getCellIndex(cellName, this._grid);

				const row = this._grid.data.getId(index.row);
				if (row !== undefined) {
					this._grid.addRowCss(row, "dhx_selected_row");
				}
				const span = isCellSpaned(cellName, merged, this._grid);
				let spanedCell = cellName;
				if (
					span &&
					!spans.includes(span) &&
					this._pressedArea !== "header" &&
					this._pressedArea !== "index"
				) {
					const { from, to } = span;
					spanedCell = `${getCellNameByIndex(from.row, from.column + 1, this._grid)}:${getCellNameByIndex(
						to.row,
						to.column + 1,
						this._grid
					)}`;
					spans.push(span);
				}
				if (isRangeId(spanedCell)) {
					this._spreadsheet.eachCell(cn => this._setCss(cn), spanedCell);
				} else {
					this._setCss(spanedCell);
				}
			});
		}
		if (filter) {
			this._spreadsheet.eachCell(cellName => {
				const index = getCellIndex(cellName, this._grid);

				const row = this._grid.data.getId(index.row);
				if (row !== undefined) {
					this._grid.addRowCss(row, "dhx_filtered_row");
				}

				const col = this._grid.config.columns[index.col];

				if (col !== undefined && !col.header[0].css.includes("dhx_filtered_header")) {
					col.header[0].css += " dhx_filtered_header";
				}
			}, filter.cell);
			this._filteredCells = filter.cell;
		}
	}
	private _setCss(name: string) {
		this._prevHeaderCss.push(name);
		const index = getCellIndex(name, this._grid);
		const row = this._grid.data.getId(index.row);
		if (row !== undefined) {
			this._grid.addRowCss(row, "dhx_selected_row");
		}

		const col = this._grid.config.columns[index.col];

		if (col !== undefined && !col.header[0].css.includes("dhx_selected_header")) {
			col.header[0].css += " dhx_selected_header";
		}
	}

	private _selectRow(row: IRow) {
		const firsCol = this._grid.config.columns[1] as any;
		const lastCol = this._grid.config.columns[this._grid.config.columns.length - 1] as any;
		this.setSelectedCell(
			`${getCellNameById(this._grid, row.id, firsCol.id)}:${getCellNameById(
				this._grid,
				row.id,
				lastCol.id
			)}`
		);
	}

	private _selectColumn(col: ICol) {
		const firstRowId = this._grid.data.getId(0);
		const lastRowId = this._grid.data.getId(this._grid.data.getLength() - 1);

		const firstRow = this._grid.data.getItem(firstRowId);
		const lastRow = this._grid.data.getItem(lastRowId);

		this.setSelectedCell(
			`${getCellNameById(this._grid, firstRow.id, col.id)}:${getCellNameById(
				this._grid,
				lastRow.id,
				col.id
			)}`
		);
	}

	private _setGroupSelectionHandlers() {
		this._grid.events.on(GridEvents.cellMouseOver, (row: IRow, col: ICol) => {
			if (getCellInfo(this.getFocusedCell(), this._activePage, this._grid).edited) {
				return;
			}
			const meta = this._activePage.getMeta();
			const focusedCell = meta.focusedCell;

			if (this._mousePressed) {
				if (this._pressedArea !== "index" && col.id === "$index") {
					return;
				}

				if (this._pressedArea === "header" && col.id !== "$index") {
					const lastRowId = this._grid.data.getId(this._grid.data.getLength() - 1);
					const lastRow = this._grid.data.getItem(lastRowId);

					this.setSelectedCell(
						`${focusedCell.cell}:${getCellNameById(this._grid, lastRow.id, col.id)}`
					);
					return;
				}

				const lastCol = this._grid.config.columns[this._grid.config.columns.length - 1] as any;
				let lastCell = getCellNameById(
					this._grid,
					row.id,
					this._pressedArea === "index" ? lastCol.id : col.id
				);
				const focused = focusedCell.cell;
				const selected = [...meta.selectedCells];

				if (this._pressedArea === "fillHandle") {
					lastCell = getLastCopyingCell(this._cellsToCopy, lastCell, this._grid);
					const lastCellInRange = this._isInRange(lastCell, this._cellsToCopy);
					if ((isRangeId(this._cellsToCopy) && lastCell === focused) || lastCellInRange) {
						lastCell = this._cellsToCopy.split(":")[1];
					}
				}

				const range = focused === lastCell ? focused : `${focused}:${lastCell}`;

				selected[selected.length - 1] = range;
				const prevFocusedCell = this.getFocusedCell();

				this.setSelectedCell(selected.join(","));
				this.setFocusedCell(prevFocusedCell);
			}
		});
		this._grid.events.on(GridEvents.headerCellMouseOver, col => {
			if (this._pressedArea === "cell") {
				return;
			}
			if (this._mousePressed && this._pressedArea === "header" && col.id !== "$index") {
				const lastRowId = this._grid.data.getId(this._grid.data.getLength() - 1);
				const lastRow = this._grid.data.getItem(lastRowId);
				const focusedCell = this._activePage.getMeta().focusedCell;
				this.setSelectedCell(
					`${focusedCell.cell}:${getCellNameById(this._grid, lastRow.id, col.id.toString())}`
				);
			}
		});
	}

	private _setHandlers() {
		let gridRect;
		let reCallTimer;

		awaitRedraw().then(() => {
			gridRect = this._grid.getRootView().node.el.getBoundingClientRect();
		});

		const moveHandler = e => {
			if (reCallTimer) {
				clearTimeout(reCallTimer);
			}
			const scrollState = this._grid.getScrollState();

			if (e.clientX > gridRect.width + gridRect.left - 50) {
				this._grid.scroll(scrollState.x + 50, scrollState.y);
			}
			if (e.clientX < gridRect.left + 50) {
				this._grid.scroll(scrollState.x - 50, scrollState.y);
			}

			if (e.clientY > gridRect.height + gridRect.top - 50) {
				this._grid.scroll(scrollState.x, scrollState.y + 50);
			}
			if (e.clientY < gridRect.top + 50) {
				this._grid.scroll(scrollState.x, scrollState.y - 50);
			}
			reCallTimer = setTimeout(() => {
				moveHandler(e);
			}, 100);
		};
		document.addEventListener("mouseup", () => {
			if (this._mousePressed) {
				this._mousePressed = false;

				const selected = this.getSelectedCell();
				if (this._pressedArea === "fillHandle") {
					if (selected !== this._cellsToCopy) {
						this._events.fire(SpreadsheetEvents.groupFill, [this._cellsToCopy, selected]);
					}
					this._cellsToCopy = "";
				}

				this._pressedArea = null;
				document.removeEventListener("mousemove", moveHandler);
				if (reCallTimer) {
					clearTimeout(reCallTimer);
				}
				if (selected) {
					this._events.fire(SpreadsheetEvents.afterSelectionSet, [this.getSelectedCell()]);
				}
				this._grid.paint();
			}
		});
		this._grid.events.on(GridEvents.cellMouseDown, (row: IRow, col: ICol, e: MouseEvent) => {
			this._mousePressed = true;
			gridRect = this._grid.getRootView().node.el.getBoundingClientRect();
			setTimeout(() => {
				if (this._mousePressed) {
					document.addEventListener("mousemove", moveHandler);
				}
			}, 100);

			const selected = this._activePage.getMeta().selectedCells;
			if (col.id === "$index") {
				const resizedRow = (e.target as HTMLElement).getAttribute("data-dhx-id");
				if (e.shiftKey) {
					const selected = this.getSelectedCell();
					let range = [];
					if (isRangeId(selected)) {
						range = selected.split(":");
					}
					let firstCellId = getCellIds(range[0] || selected, this._grid);
					const columns = this._grid.config.columns;
					const lastCell = getCellNameById(this._grid, row.id, columns[columns.length - 1].id);
					const firstCell = getCellNameById(this._grid, firstCellId.row + 1, 1);
					this.setSelectedCell(`${firstCell}:${lastCell}`);
					return;
				}
				if (!resizedRow) {
					this._pressedArea = "index";
					this._selectRow(row);
					this._mousePressed = true;
				} else {
					document.removeEventListener("mousemove", moveHandler);
					this._mousePressed = false;
					const selected = this.getSelectedCell()?.split(":");
					const first = selected && getCellIds(selected[0], this._grid);
					const last = selected && getCellIds(selected[1], this._grid);
					if (
						selected &&
						first.col === 0 &&
						last.col + 2 === this._grid.config.columns.length &&
						(+resizedRow === first.row || +resizedRow === last.row)
					) {
						this._resizedRows = selected;
					} else {
						const cell = getCellNameById(this._grid, +resizedRow + 1, 1);
						this._resizedRows = [cell, cell];
					}

					(this._spreadsheet as any)._callAction({
						cell: this._resizedRows.join(":"),
						action: Actions.resizeRow,
						groupAction: Actions.resizeRow,
						mouseEvent: e,
					});
				}
				return;
			}
			this._pressedArea = "cell";
			const cell = getCellNameById(this._grid, row.id, col.id);
			const cellInfo = getCellInfo(cell, this._activePage, this._grid);

			if (e.which === 3 && this._isInRange(cell, selected[selected.length - 1])) {
				return;
			}

			if (cellInfo.edited) {
				return;
			}
			const focusedCell = this.getFocusedCell();
			if (e.shiftKey && !e.ctrlKey && !e.metaKey) {
				this.setSelectedCell(`${focusedCell}:${getCellNameById(this._grid, row.id, col.id)}`);
				return;
			}

			if (e.shiftKey && (e.ctrlKey || e.metaKey)) {
				if (selected.length && selected[selected.length - 1] === focusedCell) {
					selected.pop();
				}

				const selectedCells = this.getSelectedCell();

				this.setSelectedCell(`${selectedCells ? selectedCells + "," : ""}${focusedCell}:${cell}`);

				return;
			}

			if ((e.ctrlKey || e.metaKey) && !e.shiftKey) {
				const selectedCells = this.getSelectedCell();
				if (!selectedCells) return;
				if (selectedCells && !getCellsArray(selectedCells, this._grid).includes(cell)) {
					return this.setSelectedCell(`${selectedCells},${cell}`);
				}
				const splitedCells = selectedCells.split(",");
				for (let i = 0; i < splitedCells.length; i++) {
					const item = splitedCells[i];
					if (!isRangeId(item) && item === cell) {
						this.setSelectedCell(splitedCells.filter(i => i !== cell).join(","));
					}
				}
				return;
			}
			this.setSelectedCell(cell);
		});

		this._grid.events.on(GridEvents.headerCellMouseDown, (col: ICol, e) => {
			const resizedColumn = (e.target as HTMLElement).getAttribute("data-dhx-id");
			if (e.shiftKey) {
				const selected = this.getSelectedCell();
				let range = [];
				if (isRangeId(selected)) {
					range = selected.split(":");
				}
				const cell = getCellIds(range[0] || selected, this._grid);
				const firstCell = getCellNameById(this._grid, this._grid.data.getId(0), cell.col + 1);
				const lastCell = getCellNameById(this._grid, this._grid.data.getLength(), col.id);
				this.setSelectedCell(`${firstCell}:${lastCell}`);
				return;
			}
			const sortIcon = (e.target as HTMLElement).className.includes("dhx_header_icon");
			if (sortIcon) {
				this._pressedArea = "header";
				this._selectColumn(col);
				return;
			}
			
			// not select when click on column resize grip [todo] make more stable check
			if (!resizedColumn || sortIcon) {
				this._pressedArea = "header";
				this._selectColumn(col);
				this._mousePressed = true;
			} else {
				if (resizedColumn === "$index") {
					// select all cells
					const first = getCellNameByIndex(0, 1, this._grid);
					const last = getCellNameByIndex(
						this._grid.data.getLength() - 1,
						this._grid.config.columns.length - 1,
						this._grid
					);
					this.setSelectedCell(`${first}:${last}`);
					return;
				}
				const selected = this.getSelectedCell()?.split(":");
				const first = selected && getCellIds(selected[0], this._grid);
				const last = selected && getCellIds(selected[1], this._grid);
				if (
					selected &&
					first.row === 0 &&
					last.row + 1 === this._grid.data.getLength() &&
					(+resizedColumn === first.col + 1 || +resizedColumn === last.col + 1)
				) {
					this._resizedColumns = selected;
				} else {
					const cell = getCellNameByIndex(0, +resizedColumn, this._grid);
					this._resizedColumns = [cell, cell];
				}

				(this._spreadsheet as any)._callAction({
					cell: this._resizedColumns.join(":"),
					action: Actions.resizeCol,
					groupAction: Actions.resizeCol,
					mouseEvent: e,
				});
			}
		});
		this._setGroupSelectionHandlers();

		this._grid.events.on(GridEvents.cellDblClick, (row: IRow, col: ICol, e: MouseEvent) => {
			if (e.ctrlKey || e.metaKey) {
				this._grid.events.fire(GridEvents.cellClick, [row, col, e]);
				return;
			}
			if (col.id !== "$index") {
				this._spreadsheet.startEdit(getCellNameById(this._grid, row.id, col.id));
			}
		});
		this._grid.events.on(GridEvents.headerCellDblClick, (col, e) => {
			const resizeGrip = (e.target as HTMLElement).getAttribute("data-dhx-id");
			if (resizeGrip === "$index") return;
			if (resizeGrip) {
				const cell = getCellNameByIndex(0, +col.id, this._grid);
				this._spreadsheet.fitColumn(cell);
			}
		});
		this._spreadsheet.events.on(SpreadsheetEvents.afterSheetChange, () => {
			this._removeHeadersCss();
			this._activePage = (this._spreadsheet as any)._activePage;
			const meta = this._activePage.getMeta();
			this.setSelectedCell(meta.selectedCells ? meta.selectedCells.join(",") : null);
			this.setFocusedCell(meta.focusedCell ? meta.focusedCell.cell : null);
		});

		this._events.on(SpreadsheetEvents.afterAction, action => {
			if (action === Actions.resizeRow) {
				this._resizedRows = null;
				this._grid.paint();
			}
			if (action === Actions.resizeCol) {
				this._resizedColumns = null;
				this._grid.paint();
			}
		});

		this._events.on(SpreadsheetEvents.gridRedraw, vm => {
			const {
				selectedCells: selected,
				focusedCell: fc,
				merged = [],
				filter,
				inserted,
			} = this._activePage.getMeta();
			const focused = fc?.cell;
			if (!selected || !focused && !this._searchedCells?.length) {
				return;
			}

			const getCellCoords = index => {
				const BORDER = 1;
				const row = this._grid.data.getItem(this._grid.data.getId(index.row));
				if (!row) {
					return {
						x: 0,
						y: 0,
						width: 0,
						height: 0,
					};
				}
				return {
					x: this._grid.config.columns
						.slice(0, index.col)
						.reduce((total, c) => (total += c.$width), 0),
					y: !index.row
						? 0
						: this._grid.data
								.mapRange(0, index.row - 1, i => i)
								.reduce((total, r) => (total += r.height || r.$height), 0),
					width: this._grid.config.columns[index.col]
						? this._grid.config.columns[index.col].$width
						: 0,
					height: row.height || row.$height - BORDER,
				};
			};
			const getGroupCoords = (rangeIndex: IRangeIndex) => {
				const start = getCellCoords(rangeIndex.start);
				const end = getCellCoords(rangeIndex.end);
				return {
					x: start.x,
					y: start.y,
					width: end.x - start.x + (end.width || 0),
					height: end.y - start.y + end.height,
				};
			};
			const { leftSplit = 0, topSplit = 0 } = this._spreadsheet.config;
			const scroll = this._grid.getScrollState();

			const fixedCoords = getCellCoords({ row: topSplit, col: leftSplit });

			const getCellInlineStyles = (cell: string = "A1") => {
				const { col, row } = getCellIds(cell, this._grid);
				const { isLeftFixed, isTopFixed } = isCellFixed(cell, leftSplit, topSplit, this._grid);
				const span = isCellSpaned(cell, merged, this._grid);
				if (span) {
					const { row, column } = span.from;
					cell = getCellNameByIndex(row, column + 1, this._grid);
				}
				let { x, y, width = 0, height } = getCellCoords(getCellIndex(cell, this._grid));

				const fixedLeft = fixedCoords.x + scroll.x + (fixedCoords.width || 0);
				const fixedTop = fixedCoords.y + scroll.y;
				let minWidth = isLeftFixed ? width: null;
				let minHeight = isTopFixed ? height : null;
				const widthDecrease = Math.abs(x - fixedLeft < 0 && !isLeftFixed ? x - fixedLeft : 0);
				const topDecrease = Math.abs(y - fixedTop < 0 && !isTopFixed ? y - fixedTop : 0);
				const left = isLeftFixed ? x + scroll.x : x;
				const top = isTopFixed ? y + scroll.y : y;
				if (span) {
					const { to, from } = span as ISpan;
					const spanCoords = getCellCoords({ row: to.row, col: +to.column + 1 });
					const spanHeight =
						from.row !== to.row ? spanCoords.y - top + spanCoords.height - height : 0;
					const spanWidth =
						from.column !== to.column ? spanCoords.x - left + spanCoords.width - width : 0;
					width = width + spanWidth;
					height = height + spanHeight;

					const spanCoordsStatic = getGroupCoords({
						start: {
							row: from.row,
							col: from.column + 1,
						},
						end: {
							row: to.row,
							col: to.column + 1,
						},
					});
					if (isLeftFixed) {
						minWidth = spanCoordsStatic.width;
					}
					if (isTopFixed) {
						minHeight = spanCoordsStatic.height;
					}
				}
				const finalWidth = width - widthDecrease;
				const finalHeight = height - topDecrease;

				return {
					width: minWidth && minWidth > finalWidth ? minWidth : finalWidth,
					height:  minHeight && minHeight > finalHeight ? minHeight : finalHeight,
					top: top + topDecrease,
					left: left + widthDecrease,
					borderLeft: !isLeftFixed && width > finalWidth ? "none" : null,
					borderRight: !isLeftFixed && finalWidth <= 0 ? "none" : null,
					borderTop: !isTopFixed && height > finalHeight ? "none" : null,
					borderBottom: !isTopFixed && finalHeight <= 0 ? "none" : null,
					position: "absolute",
					pointerEvents: "none",
					zIndex: 12,
					minWidth,
					minHeight,
				};
			};

			const getRangeInlineStyles = (cell: string) => {
				const rangeIndexs = getRangeIndexes(cell, this._grid);
				const { x, y, width: coordsWidth, height: coordsHeight } = getGroupCoords(rangeIndexs);
				const { start, end } = rangeIndexs;
				const isFixedLeft = start.col <= leftSplit;
				const isFixedTop = start.row < topSplit;
				if (isFixedLeft || isFixedTop) {
					const fixedLeft = fixedCoords.x + scroll.x + fixedCoords.width;
					const fixedTop = fixedCoords.y + scroll.y;
					const fixedCellsCoords = getGroupCoords({
						start: {
							row: start.row,
							col: start.col,
						},
						end: {
							row: topSplit && end.row >= topSplit ? topSplit - 1 : end.row,
							col: leftSplit && end.col > leftSplit ? leftSplit : end.col,
						},
					});
					const minWidth = isFixedLeft ? fixedCellsCoords.width : null;
					const minHeight = isFixedTop ? fixedCellsCoords.height : null;
					const left = isFixedLeft ? x + scroll.x : x;
					const top = isFixedTop ? y + scroll.y : y;
					const widthDecrease = Math.abs(x - fixedLeft < 0 && !isFixedLeft ? x - fixedLeft : 0);
					const topDecrease = Math.abs(y - fixedTop < 0 && !isFixedTop ? y - fixedTop : 0);
					let width = coordsWidth - widthDecrease;
					let height = coordsHeight - topDecrease;
					if (isFixedLeft) {
						width -= scroll.x;
					}
					if (isFixedTop) {
						height -= scroll.y;
					}
					return {
						top: top + topDecrease,
						left: left + widthDecrease,
						width,
						minWidth,
						height,
						borderLeft: !isFixedLeft && coordsWidth > width ? "none" : null,
						borderRight: !isFixedLeft && width <= 0 ? "none" : null,
						borderTop: !isFixedTop && coordsHeight > height ? "none" : null,
						borderBottom: !isFixedTop && height <= 0 ? "none" : null,
						minHeight,
						zIndex: 12,
					};
				}
				return {
					top: y,
					left: x,
					width: coordsWidth,
					height: coordsHeight,
					zIndex: 10,
				};
			};

			const selectedCells = selected.map(cell => {
				if (isRangeId(cell)) {
					return el(".dhx_group_selection", {
						style: getRangeInlineStyles(cell),
					});
				} else {
					if (selected.length === 1) {
						return;
					}
					return el(".dhx_group_selection", {
						style: getCellInlineStyles(cell),
					});
				}
			});
			let lastSelectedCell = selected[selected.length - 1];
			const focusedCell = el(".dhx_selected_cell", {
				style: getCellInlineStyles(focused || lastSelectedCell),
			});

			let searchedCells;

			if (this._searchCell) {
				searchedCells = this._searchedCells.map(cell => {
					return el(`.dhx_search_group${cell === this._searchCell ? ".dhx_search_cell" : ""}`, {
						style: getCellInlineStyles(cell),
					});
				});
			}

			let copySelection = [];
			const buffer = this._bufferManager.getStruct();
			if (buffer.cell && inserted !== undefined && !inserted) {
				const arrOfCells = buffer.cells.includes(",") ? buffer.cells.split(",") : [buffer.cells];
				copySelection = arrOfCells.map(copyCell => {
					const styles = isRangeId(copyCell)
						? getRangeInlineStyles(copyCell)
						: getCellInlineStyles(copyCell);
					const { width, left, height, top } = styles;
					return el(".dhx_copy_selection", {
						style: {
							...styles,
							width: width + 2, // to make it like outline
							left: left - 1,
							height: height + 2,
							top: top - 1,
						},
					});
				});
			}
			let resizedLine;
			if (this._resizedColumns) {
				const resizedId = getCellIds(this._resizedColumns[1], this._grid);
				const colIndex = findIndex(this._grid.config.columns, col => col.id == resizedId.col + 1);
				const firstCell = getCellCoords({ row: 0, col: colIndex });
				const totalHeight = this._grid.config.$totalHeight;
				const lineCenter = 1.5;
				resizedLine = el(".resize_line", {
					style: {
						top: firstCell.y,
						left: firstCell.x + firstCell.width - lineCenter,
						height: totalHeight,
					},
				});
			}
			if (this._resizedRows) {
				const resizedId = getCellIndex(this._resizedRows[1], this._grid);
				const firstCell = getCellCoords({ row: resizedId.row, col: 0 });
				resizedLine = el(".resize_line", {
					style: {
						top: firstCell.y + firstCell.height - 2,
						left: 0,
						width: this._grid.config.$totalWidth,
						height: "2px",
					},
				});
			}

			if (isRangeId(lastSelectedCell)) {
				const rangeArray = getRangeArray(lastSelectedCell, this._grid);
				lastSelectedCell = rangeArray[rangeArray.length - 1];
			}

			let filteredCells;
			if (filter) {
				const cellsArr = getCellsArray(filter.cell, this._grid).reverse();
				const lastVisibleCell = cellsArr.find(c => !this._grid.isRowHidden(getCellIds(c, this._grid).row + 1));

				filteredCells = el(".dhx_filtered_cells", {
					style: getRangeInlineStyles(`${cellsArr[cellsArr.length - 1]}:${lastVisibleCell}`),
				});
			}
			let selectionGrip;
			if (!getCellInfo(this.getFocusedCell(), this._activePage, this._grid).edited) {
				let { top, left, width, height, minWidth, minHeight } = getCellInlineStyles(lastSelectedCell);
				top += (minHeight || height) - 6;
				left += (minWidth || width) - 6;
				const { isLeftFixed, isTopFixed } = isCellFixed(lastSelectedCell, leftSplit, topSplit, this._grid);

				selectionGrip = el(".dhx_selection_grip", {
					"data-dhx-id": "selection_grip",
					style: {
						position: "absolute",
						left,
						top,
						zIndex:
							(!isTopFixed && !isLeftFixed) ||
							(isTopFixed && width <= 0) ||
							(isLeftFixed && height <= 0)
								? 10
								: 12, // zIndex 10 if under fixed cells
					},
					onmousedown: () => {
						this._mousePressed = true;
						document.addEventListener("mousemove", moveHandler);

						this._pressedArea = "fillHandle";
						let selected = this.getSelectedCell().split(",");
						const lastCell = selected[selected.length - 1];
						this.setSelectedCell(lastCell);
						//check if the last cell is spaned
						const cellsArr = getCellsArray(lastCell, this._grid);
						const span = isCellSpaned(cellsArr[cellsArr.length - 1], merged, this._grid);
						this._cellsToCopy = lastCell;
						if (span) {
							const { to } = span;
							const lastCell = getCellNameByIndex(to.row, to.column + 1, this._grid);
							this._cellsToCopy = `${cellsArr[0]}:${lastCell}`;
						}
					},
				});
			}

			vm.refs.selection.patch(
				el(".dhx_grid_selection", [
					...copySelection,
					focusedCell,
					...selectedCells,
					searchedCells,
					resizedLine,
					filteredCells,
					selectionGrip,
				])
			);
		});
	}
}
